<template>
  <transition name="cart-modal">
    <article class="aside-cart">
      <transition name="modal-backdrop">
        <div class="aside-cart__backdrop" @mouseup="handleCloseClick" @touchend.prevent="handleCloseClick"></div>
      </transition>
      <transition v-show="modalStore.isCartModalOpen" name="modal-contents">
        <aside class="modal-cart">
          <div class="base-modal__contents">
            <header class="base-modal__header">
              <BaseTitle :title="t('title.cart')">
                <template #icon>
                  <img src="@/assets/images/icon-cart.svg" alt="장바구니 아이콘" />
                </template>
              </BaseTitle>
            </header>

            <div class="modal-cart__body">
              <div v-if="cartStore.cart.CartMenus.length === 0 || menuStore.menus.length === 0" class="empty-data">
                {{ t('info.cart_empty') }}
              </div>
              <ul v-else class="modal-cart__ul list-style-none">
                <li v-for="(cartMenu, index) in cartStore.cart.CartMenus" :key="index">
                  <CartItem :index="index" :cart-menu="cartMenu" />
                </li>
              </ul>
            </div>

            <footer class="modal-cart__footer">
              <div class="modal-cart__top">
                <div class="modal-cart__count">
                  <span>{{ cartStore.totalMenuTypeQty }}</span> {{ t('label.cart_count_type') }}
                  <span>{{ cartStore.totalQty }}</span> {{ t('label.cart_count_piece') }}
                </div>
                <div class="modal-cart__amount">
                  <span class="amount__text">{{ t('label.order_amount') }}</span>
                  <span class="amount__price">{{ formatCurrency(cartStore.totalAmount) }}</span>
                </div>
              </div>
              <div class="modal-cart__button">
                <div class="button__container">
                  <BaseButtonGroup>
                    <BaseButton class="outline" :flex="1" @button-click="handleCloseClick">
                      <template #icon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="25.075"
                          height="25.075"
                          viewBox="0 0 25.075 25.075"
                        >
                          <path
                            fill="#ffffff"
                            d="M24.258,100.207a2.509,2.509,0,1,0-3.548-3.548l-8.247,8.255L4.207,96.667a2.509,2.509,0,1,0-3.548,3.548l8.255,8.247L.667,116.718a2.509,2.509,0,1,0,3.548,3.548l8.247-8.255,8.255,8.247a2.509,2.509,0,1,0,3.548-3.548l-8.255-8.247Z"
                            transform="translate(0.075 -95.925)"
                          />
                        </svg>
                      </template>
                      {{ t('button.close') }}
                    </BaseButton>
                    <BaseButton :flex="2" @button-click="handleOrderClick">{{
                      configStore.usePayment && !configStore.usePreOrder ? t('button.pay') : t('button.order')
                    }}</BaseButton>
                  </BaseButtonGroup>
                </div>
              </div>
            </footer>
          </div>
        </aside>
      </transition>
    </article>
  </transition>
</template>

<script setup lang="ts">
  import CartItem from '@/components/CartItem.vue';
  import OrderComplete from '@/components/OrderComplete.vue';
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import BaseTitle from '@/components/base/BaseTitle.vue';
  import { useCart } from '@/composable/useCart';
  import { useMinOrderQtyValidator } from '@/composable/useMinOrderQtyValidator';
  import { usePayment } from '@/composable/usePayment';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import UserError from '@/error';
  import { useCartStore } from '@/stores/cart';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { useModalStore } from '@/stores/modal';
  import { useOrderStore } from '@/stores/order';
  import { usePaymentStore } from '@/stores/payment';
  import { formatCurrency, getNameByLocale } from '@/utils';
  import { useI18n } from 'vue-i18n';
  import Payment from './Payment.vue';

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const menuStore = useMenuStore();
  const cartStore = useCartStore();
  const paymentStore = usePaymentStore();
  const orderStore = useOrderStore();

  const { ensureHeadcountAsync } = useCart();
  const { createPaymentSummaryFromCart, createPaymentSplit } = usePayment();
  const { assertMinOrderQty } = useMinOrderQtyValidator();

  /** 닫기 버튼 클릭 */
  const handleCloseClick = () => modalStore.closeCartModal();

  /** 주문하기 또는 결제하기 버튼 클릭 */
  const handleOrderClick = async () => {
    if (!cartStore.cart.CartMenus || cartStore.cart.CartMenus.length === 0) {
      throw new UserError(t('error.empty_cart'));
    }

    await order();
  };

  /** 주문 진행 */
  const order = async () => {
    const loadingId = commonStore.showLoading();
    try {
      // POS에서 주문 조회 후 세팅
      await orderStore.fetchOrder(false);

      // 최소 주문 금액 검사
      if (configStore.config.IsMinOrderAmountUsed && configStore.config.MinOrderAmount > 0) {
        // 장바구니 금액과 선주문한 금액의 합이 최소주문금액 보다 작은지 검사
        if (cartStore.totalAmount + (orderStore.order.AMT ?? 0) < configStore.config.MinOrderAmount) {
          throw new UserError(t('error.required_min_amount', [formatCurrency(configStore.config.MinOrderAmount)]));
        }
      }

      // 최소 주문 수량 검증
      assertMinOrderQty(cartStore.cart.CartMenus);

      // 장바구니 메뉴 품절 및 한정수량 검사
      await validateCartSoldOut();

      // POS 주문 데이터에 인원 수가 있다면 POS 인원 수로 업데이트
      // 태블릿에 입력되어 있는 경우 POS 인원 수로 덮어쓰기 (1인 1메뉴로 인원 수를 미리 입력한 경우)
      if (orderStore.order.CustomerCnt > 0) {
        cartStore.setHeadcount(orderStore.order.CustomerCnt);
      }

      // 인원 수 유효성 검사 및 조정. 검사 실패 시 주문 프로세스 중단
      if (!(await ensureHeadcountAsync())) return;

      // 1인 1메뉴 옵션을 사용하는 경우, 주메뉴 수량 확인을 위해 POS 주문 데이터 조회
      if (configStore.useOneMenuPerPerson) {
        // 인원수 입력 여부 검사
        if (!cartStore.cart.Headcount) throw new UserError(t('error.required_headcount'));

        // 장바구니 메인메뉴 수량 조회
        const cartMainMenuQty =
          cartStore.cart.CartMenus?.reduce((acc, cartMenu) => {
            return acc + (menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode)?.MainMenuQty || 0) * cartMenu.Qty;
          }, 0) || 0;

        // POS 주문 데이터 메인메뉴 수량 조회
        const orderMainMenuQty =
          orderStore.order.Items?.reduce((acc, item) => {
            return acc + (menuStore.getMenu(null, item.ITEM_CODE)?.MainMenuQty || 0) * item.QTY;
          }, 0) || 0;

        // 인원수와 메인메뉴 수량으로 1인 1메뉴 여부 검사
        if (cartStore.cart.Headcount > cartMainMenuQty + orderMainMenuQty) {
          throw new UserError(t('error.one_menu_per_person'));
        }
      }

      // 결제 기능을 사용하고 결제 금액이 0보다 큰 경우 결제 열고 종료.
      if (configStore.usePayment && !configStore.usePreOrder && cartStore.finalAmount > 0) {
        // 결제 모달 열기
        modalStore.openFullScreenModal(Payment, { isPreOrder: false, paymentSummary: createPaymentSummaryFromCart() });
        return;
      }

      // 결제 기능을 사용하는데 결제 금액이 0인 경우 현금 결제로 처리
      if (configStore.usePayment && !configStore.usePreOrder && cartStore.finalAmount === 0) {
        // 결제 요약 데이터 세팅
        paymentStore.setSummary(createPaymentSummaryFromCart());
        // 현금 결제 데이터 추가
        paymentStore.addPaymentSplit(createPaymentSplit(PaymentMethod.Cash));
      }

      // 장바구니 데이터를 주문 데이터에 추가
      orderStore.addOrderItem(cartStore.cart.CartMenus);

      // 주문 전송
      await orderStore.sendOrder(false, false);

      // 장바구니 모달 창 닫기
      modalStore.closeCartModal();

      // 주문 완료 모달 열기
      modalStore.openFullScreenModal(OrderComplete, { hasPayment: false });
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };

  /** 장바구니 메뉴 일반 품절, 한정수량 품절 검사 */
  const validateCartSoldOut = async () => {
    // 실시간 품절 검사 및 한정수량 사용 여부
    const { IsRealtimeSoldOutUsed, IsLimitedItemUsed } = configStore.config;

    // 둘 다 사용하지 않는 경우 검사하지 않는다
    if (!IsRealtimeSoldOutUsed && !IsLimitedItemUsed) return;

    if (IsRealtimeSoldOutUsed) {
      // 일반 품절 데이터 갱신
      await menuStore.fetchSoldOut();
    }

    // 한정수량 옵션 사용하는 경우 한정수량 데이터 갱신
    if (IsLimitedItemUsed) {
      await menuStore.fetchLimitItem();
    }

    // 품절 검사 에러 메시지
    let errorMessage = '';
    for (const cartMenu of cartStore.cart.CartMenus) {
      // 메뉴 상세 데이터 조회
      const menu = menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode);
      const menuName = getNameByLocale(menu);

      if (IsRealtimeSoldOutUsed) {
        // 일반 품절 검사
        if (menu.IsSoldOut) {
          errorMessage += t('error.item_soldout', { name: menuName }) + '\n';
        } else {
          // 토핑 품절 검사
          const toppingNames = cartMenu.Toppings.reduce<string[]>((names, option) => {
            const topping = menuStore.getTopping(option.GroupCode.toString(), option.OptionCode);
            if (topping.IsSoldOut) {
              names.push(getNameByLocale(topping));
            }
            return names;
          }, []).join(' / ');
          if (toppingNames) errorMessage += t('error.item_soldout', { name: `${menuName} - [${toppingNames}]` }) + '\n';

          // 세트 메뉴 품절 검사
          const setMenuNames = cartMenu.SetMenus.reduce<string[]>((names, option) => {
            const setMenu = menuStore.getSetMenu(option.MenuItemCode, Number(option.GroupCode), option.OptionCode);
            if (setMenu.IsSoldOut) {
              names.push(getNameByLocale(setMenu));
            }
            return names;
          }, []).join(' / ');
          if (setMenuNames) errorMessage += t('error.item_soldout', { name: `${menuName} - [${setMenuNames}]` }) + '\n';
        }
      }

      // 한정수량 품절 검사 (중복 에레메시지 생성을 막기 위해 메뉴가 품절이 아닌 경우만 검사)
      if (!menu.IsSoldOut && configStore.config.IsLimitedItemUsed) {
        // 현재 메뉴의 한정수량 데이터 조회
        const limitItem = menuStore.getLimitItem(cartMenu.ItemCode);

        // 한정수량 데이터가 존재한다면 수량 검사
        if (limitItem && limitItem.OrderLimitCnt < cartMenu.Qty) {
          if (limitItem.OrderLimitCnt === 0) {
            errorMessage += t('error.item_soldout', { name: menuName }) + '\n';
          } else {
            errorMessage += t('error.limit_item2', { name: menuName, count: limitItem.OrderLimitCnt }) + '\n';
          }
        }
      }
    }

    // 품절 또는 한정수량 메뉴가 있다면 에러 메시지 표시
    if (errorMessage) {
      throw new UserError(errorMessage.trim());
    }
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .aside-cart {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: vars.$z-index-modal;
    .aside-cart__backdrop {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
    }
  }

  .modal-cart {
    position: relative;
    z-index: 1;
    max-width: 44.7761vw;
    height: 100%;
    margin-left: auto;
    background: #fff;
    .base-modal__contents {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .base-modal__header {
      position: relative;
      display: flex;
      align-items: center;
      padding: 1.6418vw 1.9403vw;
      border-bottom: 0.0746vw solid var(--border-color-secondary);
      .header__figure {
        width: 2.6866vw;
        margin-right: 0.7463vw;
        line-height: 1;
        img {
          width: 100%;
        }
      }
      .modal-cart__close {
        position: absolute;
        top: 50%;
        right: 1.6418vw;
        transform: translateY(-50%);
        // Todo: (Add) CSS 추가
        line-height: 0;
        img {
          width: 1.923vw;
        }
        // Todo: (Add) CSS 추가
      }
    }
    .base-modal__title {
      font-size: 2.0896vw;
      font-weight: 700;
      @media (max-width: vars.$md) {
        font-size: 16px;
      }
      .title__typography {
        position: relative;
        z-index: 1;
      }
    }
    .modal-cart__body {
      flex: 1;
      overflow-y: auto;

      .empty-data {
        font-size: 1.5625vw;
      }
    }
    .modal-cart__ul {
      padding: 0 1.6418vw;
      li {
        padding: 1.4925vw 0;
      }
      li + li {
        border-top: 0.0746vw dashed var(--border-color-primary);
      }
    }

    .modal-cart__top {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 6.2687vw;
      padding: 0 1.6418vw;
      background: #efefef;
      box-shadow: 0 -0.2239vw 0.2239vw #7474744d;
    }

    .modal-cart__count {
      font-size: 1.8657vw;
      font-weight: 700;
      span {
        font-size: 2.3881vw;
        color: var(--point-color-secondary);
      }
    }

    .modal-cart__amount {
      display: flex;
      align-items: baseline;
      justify-content: center;
      .amount__text {
        font-size: 1.8657vw;
        font-weight: 700;
        margin-right: 1.0448vw;
      }
      .amount__price {
        font-size: 2.3881vw;
        font-weight: 700;
        color: var(--point-color-secondary);
      }
    }
    .modal-cart__button {
      padding: 1.3433vw 1.6418vw;
      background: #272727;
    }
    @media (max-width: vars.$lg) {
      max-width: 640px;
    }
    @media (max-width: vars.$md) {
      max-width: 100vw;
      .base-modal__header {
        padding: 10px;
        border-bottom-width: 1px;
        .header__figure {
          width: 24px;
          margin-right: 6px;
        }
        .modal-cart__close {
          right: 8px;
          img {
            width: 14px;
          }
        }
      }
      .base-modal__title {
        font-size: 18px;
        @media (max-width: vars.$md) {
          font-size: 16px;
        }
      }
      .modal-cart__body {
        height: calc(100% - 145px);
        overflow-y: auto;
      }
      .modal-cart__count {
        font-size: 14px;
        span {
          font-size: 18px;
        }
      }
      .modal-cart__amount {
        .amount__text {
          font-size: 14px;
          margin-right: 8px;
        }
        .amount__price {
          font-size: 18px;
        }
      }
      .modal-cart__top {
        height: 40px;
        padding: 0 10px;
      }
      .modal-cart__ul {
        padding: 5px;
        li {
          padding: 10px;
          border-top-width: 1px;
        }
      }
    }
  }

  // 장바구니 모달 전체 show/hide
  .cart-modal-enter-active,
  .cart-modal-leave-active {
    visibility: visible;
  }

  .cart-modal-enter-from,
  .cart-modal-leave-to {
    transition-delay: 0.22s;
    visibility: hidden;
  }

  // 장바구니 slide in/out
  .modal-contents-enter-active,
  .modal-contents-leave-active {
    transform: translateX(0);
    transition: 0.2s ease-in;
  }

  .modal-contents-enter-from,
  .modal-contents-leave-to {
    transform: translateX(100%);
  }

  // backdrop fade in/out
  .modal-backdrop-enter-active,
  .modal-backdrop-leave-active {
    opacity: 1;
    transition: 0.2s ease-in;
  }

  .modal-backdrop-enter-from,
  .modal-backdrop-leave-to {
    opacity: 0;
  }
</style>
